<template>
  <v-form
    ref="eventFormRef"
    :id="formId"
    class="form form--with-sticky-actions page-stack grid-stack grid-gap-page-section event-form"
    @submit.prevent="submitEventFormAsync"
  >
    <section class="page-panel">
      <header class="page-panel__header">
        <h3 class="panel-title">Osnovni podaci</h3>
      </header>

      <div class="page-panel__content grid-stack grid-row-gap-18">
        <AppSelect
          v-model="eventFormValues.brandCode"
          class="event-form__basic-field"
          :items="brandOptions"
          label="Marka"
          required
        />

        <AppTextField
          v-model="eventFormValues.name"
          class="event-form__basic-field"
          label="Naziv događaja"
          placeholder="npr. Test drive VW January 2026"
          required
        />

        <div class="grid grid-column-gap-16">
          <div class="col-12 col-sm-6">
            <AppDateField
              v-model="eventFormValues.startDate"
              label="Datum početka"
              required
            />
          </div>

          <div class="col-12 col-sm-6">
            <AppTimeField
              v-model="eventFormValues.startTime"
              label="Vrijeme početka"
              required
            />
          </div>
        </div>

        <div class="grid grid-column-gap-16">
          <div class="col-12 col-sm-6">
            <AppDateField
              v-model="eventFormValues.endDate"
              label="Datum završetka"
              required
            />
          </div>

          <div class="col-12 col-sm-6">
            <AppTimeField
              v-model="eventFormValues.endTime"
              label="Vrijeme završetka"
              required
            />
          </div>
        </div>

        <AppTextField
          v-model="eventFormValues.location"
          label="Lokacija"
          placeholder="npr. Zagreb, Porsche Center"
          required
        />
      </div>
    </section>

    <section class="page-panel">
      <header class="page-panel__header page-panel__header--with-action">
        <div>
          <h3 class="panel-title">Upitnici</h3>
          <p class="panel-description">Dodajte poveznice na upitnike koje uzvanici trebaju ispuniti</p>
        </div>

        <v-btn
          class="button button--secondary button--small button--full-mobile event-form__add-field-button"
          prepend-icon="mdi-plus"
          variant="outlined"
          @click="addQuestionnaireRow('form')"
        >
          Dodaj polje
        </v-btn>
      </header>

      <div class="page-panel__content grid-stack grid-row-gap-18">
        <div
          v-for="(questionnaire, index) in eventFormValues.questionnaires.filter(({ type }) => type === 'form')"
          :key="index"
          class="grid grid-column-gap-16"
        >
          <div class="col-12 col-sm-6">
            <AppTextField
              v-model="questionnaire.name"
              label="Naziv upitnika"
              placeholder="npr. Ocjena školovanja"
              :name="`questionnaire-name-${index}`"
            />
          </div>

          <div class="col-12 col-sm-6">
            <AppTextField
              v-model="questionnaire.url"
              label="URL upitnika"
              placeholder="npr. www.microsoft-forms.com/23498230"
              :name="`questionnaire-url-${index}`"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="page-panel">
      <header class="page-panel__header page-panel__header--with-action">
        <div>
          <h3 class="panel-title">Rezultati upitnika</h3>
          <p class="panel-description">Dodajte poveznice na rezultate upitnika</p>
        </div>

        <v-btn
          class="button button--secondary button--small button--full-mobile event-form__add-field-button"
          prepend-icon="mdi-plus"
          variant="outlined"
          @click="addQuestionnaireRow('result')"
        >
          Dodaj polje
        </v-btn>
      </header>

      <div class="page-panel__content grid-stack grid-row-gap-18">
        <div
          v-for="(questionnaireResult, index) in eventFormValues.questionnaires.filter(({ type }) => type === 'result')"
          :key="index"
          class="grid grid-column-gap-16"
        >
          <div class="col-12 col-sm-6">
            <AppTextField
              v-model="questionnaireResult.name"
              label="Naziv rezultata upitnika"
              placeholder="npr. Ocjena školovanja"
              :name="`questionnaire-result-name-${index}`"
            />
          </div>

          <div class="col-12 col-sm-6">
            <AppTextField
              v-model="questionnaireResult.url"
              label="URL rezultata upitnika"
              placeholder="npr. www.microsoft-forms.com/23498230"
              :name="`questionnaire-result-url-${index}`"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="page-panel">
      <header class="page-panel__header">
        <h3 class="panel-title">Potrebni dokumenti</h3>
        <p class="panel-description">Odaberite dokumente koje uzvanici trebaju potpisati za događaj</p>
      </header>

      <div class="event-form__option-list grid-stack grid-gap-10">
        <div
          v-for="documentType in state.documentTypes"
          :key="documentType.id"
          class="option event-form__option"
          :class="{ 'event-form__option--with-fields': documentType.eventCustomFieldPlaceholder }"
        >
          <v-checkbox
            v-model="eventFormValues.documents"
            class="checkbox event-form__checkbox"
            density="compact"
            hide-details
            :label="documentType.name"
            :value="documentType.eventFormValue"
          />

          <AppTextField
            v-if="documentType.eventCustomFieldPlaceholder && eventFormValues.documents.includes(documentType.eventFormValue)"
            v-model="documentType.eventFormValue.customFieldValue"
            :label="documentType.eventCustomFieldPlaceholder"
            placeholder="Unesite"
            width="470px"
            required
          />
        </div>
      </div>
    </section>

    <section class="page-panel">
      <header class="page-panel__header">
        <h3 class="panel-title">Aktivnosti</h3>
        <p class="panel-description">Odaberite aktivnosti koje će biti dostupne tijekom događaja</p>
      </header>

      <div class="event-form__option-list grid-stack grid-gap-10">
        <div
          v-for="activityType in state.activityTypes"
          :key="activityType.id"
          class="option event-form__option"
          :class="{ 'event-form__option--activity-fields': activityType.customFieldPlaceholder }"
        >
          <v-checkbox
            v-model="eventFormValues.activities"
            class="checkbox event-form__checkbox"
            density="compact"
            hide-details
            :label="activityType.name"
            :value="activityType.eventFormValue"
          />

          <AppTextField
            v-if="activityType.customFieldPlaceholder && eventFormValues.activities.includes(activityType.eventFormValue)"
            v-model="activityType.eventFormValue.customFieldValue"
            :label="activityType.customFieldPlaceholder"
            placeholder="Unesite"
            width="330px"
          />
        </div>
      </div>
    </section>

    <section class="page-panel">
      <header class="page-panel__header">
        <h3 class="panel-title">Napomene</h3>
        <p class="panel-description">Dodajte dodatne napomene ili upute za ovaj događaj</p>
      </header>

      <AppTextarea
        v-model="eventFormValues.comment"
        placeholder="Unesite napomene ovdje..."
        :rows="6"
      />
    </section>

    <div class="submit-bar">
      <ToolbarActionButton
        :text="submitText"
        type="submit"
        :form="formId"
        icon=""
        :disabled="isSavingEvent || !state.areFormOptionsLoaded"
        :loading="isSavingEvent"
      />
    </div>
  </v-form>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'

import AppDateField from './AppDateField.vue'
import AppSelect from './AppSelect.vue'
import AppTextarea from './AppTextarea.vue'
import AppTextField from './AppTextField.vue'
import AppTimeField from './AppTimeField.vue'
import ToolbarActionButton from './ToolbarActionButton.vue'
import { canAccessBrand } from '../auth/authorization'
import { datetime, getEventBrandOptions } from '../helpers'
import { apiClient } from '../services/apiClient'
import { showErrorToast, showRequestErrorToast } from '../store/appStore'

const props = defineProps({
  formId: {
    type: String,
    default: 'event-form',
  },

  initialEventValues: {
    type: Object,
    default: null,
  },

  submitText: {
    type: String,
    required: true,
  },

  isSavingEvent: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['submit'])

const brandOptions = getEventBrandOptions().filter(({ value }) => canAccessBrand(value))
const state = reactive({
  activityTypes: [],
  documentTypes: [],
  areFormOptionsLoaded: false,
})
const eventFormRef = ref(null)
const eventFormValues = reactive(createEventFormValues(props.initialEventValues))

onMounted(loadFormOptionsAsync)

async function loadFormOptionsAsync () {
  state.areFormOptionsLoaded = false

  try {
    const [documentTypes, activityTypes] = await Promise.all([
      apiClient.getAsync('/api/document-types'),
      apiClient.getAsync('/api/activity-types'),
    ])

    state.documentTypes = mapEventFormOptions(documentTypes, eventFormValues.documents)
    state.activityTypes = mapEventFormOptions(activityTypes, eventFormValues.activities)
      .sort((firstActivity, secondActivity) => firstActivity.orderIndex - secondActivity.orderIndex)

    state.areFormOptionsLoaded = true
  } catch (error) {
    console.error('Failed to load event form options.', error)
    showRequestErrorToast(error, 'Podatke obrasca nije bilo moguće učitati.')
  }
}

function mapEventFormOptions (options, selectedValues) {
  return options.map((option) => ({
    ...option,
    eventFormValue: selectedValues.find(({ id }) => id === option.id) ?? {
      id: option.id,
      customFieldValue: null,
    },
  }))
}

function createQuestionnaireRow (type) {
  return {
    name: '',
    type,
    url: '',
  }
}

function createEventFormValues (initialEventValues) {
  return {
    brandCode: initialEventValues?.brandCode ?? null,
    name: initialEventValues?.name ?? '',
    startDate: initialEventValues?.startDate ?? '',
    startTime: initialEventValues?.startTime ?? '',
    endDate: initialEventValues?.endDate ?? '',
    endTime: initialEventValues?.endTime ?? '',
    location: initialEventValues?.location ?? '',
    questionnaires: initialEventValues?.questionnaires?.map(({ name, type, url }) => ({ name, type, url })) ?? [
      createQuestionnaireRow('form'),
      createQuestionnaireRow('result'),
    ],
    documents: initialEventValues?.documents?.map(({ id, customFieldValue }) => ({
      id,
      customFieldValue: customFieldValue ?? null,
    })) ?? [],
    activities: initialEventValues?.activities?.map(({ id, customFieldValue }) => ({
      id,
      customFieldValue: customFieldValue ?? null,
    })) ?? [],
    comment: initialEventValues?.comment ?? '',
  }
}

function addQuestionnaireRow (type) {
  eventFormValues.questionnaires.push(createQuestionnaireRow(type))
}

async function submitEventFormAsync () {
  if (props.isSavingEvent || !state.areFormOptionsLoaded) {
    return
  }

  const validationResult = await eventFormRef.value.validate()

  if (!validationResult.valid) {
    return
  }

  const startTimestamp = datetime(`${eventFormValues.startDate}T${eventFormValues.startTime}`)
  const endTimestamp = datetime(`${eventFormValues.endDate}T${eventFormValues.endTime}`)

  if (endTimestamp < startTimestamp) {
    showErrorToast('Vrijeme završetka ne može biti prije vremena početka.')
    return
  }

  const submittedEventValues = {
    ...eventFormValues,
    questionnaires: eventFormValues.questionnaires.filter(({ name, url }) => name && url),
    comment: eventFormValues.comment || null,
  }

  emit('submit', submittedEventValues)
}
</script>

<style
  scoped
  lang="scss"
>
.event-form {
  width: 100%;

  &__add-field-button {
    flex: 0 0 auto;
    min-width: 128px;
  }

  &__basic-field {
    @include breakpoint-up("sm") {
      max-width: 560px;
    }
  }

  &__option {
    &--with-fields {
      gap: 20px;

      .event-form__checkbox {
        @include breakpoint-up("sm") {
          flex: 0 0 176px;
        }
      }
    }

    &--activity-fields {
      gap: 20px;

      .event-form__checkbox {
        @include breakpoint-up("sm") {
          flex: 0 0 96px;
        }
      }
    }
  }
}
</style>
